/**
 * @fileoverview SessionCard Component for Virtual Test Center
 * 
 * Displays an individual proctoring session in a card format with live video feed,
 * student information, notification badges, and action controls.
 * 
 * @module vtc/components/SessionCard
 */

import PropTypes from 'prop-types';
import { memo, useEffect, useMemo, useState } from 'react';
import Card from '../Common/Card';
import SessionCardHeader from './SessionCardHeader';
import SessionCardBody from './SessionCardBody';
import SessionCardProgress from './SessionCardProgress';
import { useSessionCardState } from './useSessionCardState';
import useSessionStore from '../../stores/session';
import './styles.css';

/**
 * SessionCard Component
 * 
 * Renders a card displaying a live proctoring session with video feed, student info,
 * and action controls. The card shows different UI states based on whether the
 * session is focused and displays real-time notifications for messages and flags.
 * 
 * **Features:**
 * - Live video feed via SessionViewer
 * - Student name display in header
 * - Notification badges (unread messages, flags)
 * - Action menu (transfer, end session)
 * - Focus mode toggle
 * - Loading states with spinner overlay
 * - Sound notifications for new messages
 * - Progress indicator
 * 
 * **Visual States:**
 * - **Unfocused**: Blue header with full controls
 * - **Focused**: Red header with "Focused" label, minimal controls
 * - **Loading**: Semi-transparent overlay with spinner
 * - **Reconnecting**: Loading overlay with reconnection message
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.sessionSid - Unique session identifier
 * @param {Object} props.data - Session data object
 * @param {string} props.data.firstName - Student's first name
 * @param {string} props.data.lastName - Student's last name
 * @param {string} props.data.installSid - Installation SID for the exam
 * @param {string} props.data.examSid - Exam SID
 * @returns {JSX.Element} The session card component
 * 
 * @example
 * // Basic usage in SessionGrid
 * <SessionCard
 *   sessionSid="session_123"
 *   data={{
 *     firstName: "John",
 *     lastName: "Doe",
 *     installSid: "install_456",
 *     examSid: "exam_789"
 *   }}
 * />
 */
function SessionCard({ sessionSid }) {
  // Use custom hook to manage all state and logic
  const {
    isFocused,
    showLoader,
    loaderMessage,
    cardViewer,
  } = useSessionCardState(sessionSid);

  const noise = useSessionStore((state) => state.noisySessions[sessionSid] || null);
  const data = useSessionStore((state) => {
    // Check active sessions first, then fall back to exited sessions
    return state.sessions[sessionSid]?.sessionData || state.exitedSessions[sessionSid]?.sessionData;
  });

  const [isNoiseDetected, setIsNoiseDetected] = useState(false);
  const [isTalkingDetected, setIsTalkingDetected] = useState(false);

  useEffect(() => {
    if (noise) {
      // only say there is noise detected if this session is not focused by any one...
      const isFocusedByAny = useSessionStore.getState().isFocusedByAnyProctor(sessionSid);

      if (!isFocusedByAny) {
        setIsNoiseDetected(noise.hasNoise);
        return
      }
      if (!noise.hasNoise && isNoiseDetected) {
        setIsNoiseDetected(false);
      }
    } else {
      setIsNoiseDetected(false);
    }
  }, [noise, sessionSid, isNoiseDetected]);

  // Memoize SessionCardBody to prevent re-renders when noise detection state changes
  // MUST be before early returns to comply with Rules of Hooks
  // Note: showLoader and loaderMessage are passed but not used (loader is commented out)
  // so they're excluded from dependencies to prevent unnecessary re-renders
  const memoizedBody = useMemo(() => (
    <SessionCardBody
      sessionSid={sessionSid}
      cardViewer={cardViewer}
      showLoader={showLoader}
      loaderMessage={loaderMessage}
    />
  ), [sessionSid, cardViewer, showLoader, loaderMessage]);

  // Memoize SessionCardHeader to prevent re-renders
  // Header gets its own state from stores, so only re-render when sessionSid or data changes
  const memoizedHeader = useMemo(() => (
    <SessionCardHeader
      sessionSid={sessionSid}
      data={data}
    />
  ), [sessionSid, data]);

  // Memoize SessionCardProgress to prevent re-renders
  const memoizedProgress = useMemo(() => (
    <SessionCardProgress sessionSid={sessionSid} />
  ), [sessionSid]);

  // Don't render card when focused
  if (isFocused) {
    return null;
  }

  // if there is no data, don't render the card
  if (!data) {
    return null;
  }

  return (
    <Card className={`session-card ${isNoiseDetected ? 'noisy' : ''} ${isTalkingDetected ? 'talking' : ''}`}
      borderWidth="medium">
      {/* Header with student name, badges, and action buttons */}
      {memoizedHeader}

      {/* Progress bar - Updates independently to prevent re-rendering entire card */}
      {memoizedProgress}

      {/* Body with video feed portal - Memoized to prevent re-renders from noise detection */}
      {memoizedBody}
    </Card>
  );
}

/**
 * PropTypes validation
 */
SessionCard.propTypes = {
  /** Unique session identifier */
  sessionSid: PropTypes.string.isRequired,
};

/**
 * Export memoized component
 * Prevents unnecessary re-renders when props haven't changed
 */
export default memo(SessionCard);
